import { ArrowUpRight, PlayCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ReleaseNotes } from '@/components/ui/release-notes';
import type { HelpScreenLink, HelpTopic, HelpTourLink } from '@/lib/help/help-resolve';

export interface HelpPanelText {
  purpose: string;
  steps: string;
  connected: string;
  tour: string;
  noSteps: string;
}

export const HELP_PANEL_TEXT: HelpPanelText = {
  purpose: 'What this screen is for',
  steps: 'How to use it',
  connected: 'Connected screens',
  tour: 'Watch in the tour',
  noSteps: 'Step-by-step help for this screen is on its way. The menu entry and the module it belongs to are described above.',
};

function SectionLabel({ children }: { children: string }) {
  return <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{children}</p>;
}

export function HelpPanel({
  topic,
  screens,
  tour,
  text = HELP_PANEL_TEXT,
}: {
  topic: HelpTopic;
  screens: HelpScreenLink[];
  tour?: HelpTourLink;
  text?: HelpPanelText;
}) {
  return (
    <div className="space-y-6 text-sm">
      <section className="space-y-2">
        <SectionLabel>{text.purpose}</SectionLabel>
        <ReleaseNotes notes={topic.body} emptyText="" className="text-foreground/90" />
      </section>

      <section className="space-y-2">
        <SectionLabel>{text.steps}</SectionLabel>
        {topic.steps.length > 0 ? (
          <ol className="space-y-2">
            {topic.steps.map((step, index) => (
              <li key={step} className="flex gap-3 leading-6">
                <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/10 text-[11px] font-semibold tabular text-primary">
                  {index + 1}
                </span>
                <span className="min-w-0">{step}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="leading-6 text-muted-foreground">{text.noSteps}</p>
        )}
      </section>

      {tour && (
        <section className="space-y-2">
          <SectionLabel>{text.tour}</SectionLabel>
          <a
            href={tour.href}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 rounded-lg border bg-muted/40 p-3 transition-colors hover:border-primary/50 hover:bg-primary/5 focus-ring"
          >
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground">
              <PlayCircle className="h-5 w-5" aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="block truncate font-medium">{tour.heading}</span>
              <span className="block text-xs text-muted-foreground">{text.tour}</span>
            </span>
            <ArrowUpRight className="ms-auto h-4 w-4 shrink-0 text-muted-foreground rtl:-scale-x-100" aria-hidden />
          </a>
        </section>
      )}

      {screens.length > 0 && (
        <section className="space-y-2">
          <SectionLabel>{text.connected}</SectionLabel>
          <ul className="flex flex-wrap gap-2">
            {screens.map((screen) => (
              <li key={screen.to}>
                <Link
                  to={screen.to}
                  className="inline-flex items-center gap-1 rounded-full border bg-card px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary focus-ring"
                >
                  {screen.label}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
